import React, { useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  HStack,
  Input,
  SimpleGrid,
  Text,
  useToast,
} from '@chakra-ui/react';
import { client } from '@php-beam/packages/frontend/src/api/client';
import { DocumentTag, LeadDoc } from './types';

export default function GenericDocumentSection({
  leadId,
  tags,
  existingDocs,
  requiredForSubmission,
  onDone,
}: {
  leadId: number;
  tags: DocumentTag[];
  existingDocs: LeadDoc[];
  requiredForSubmission: string[];
  onDone: () => void;
}) {
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState('');
  const toast = useToast();

  const existingTags = new Set(existingDocs.map((d) => d.tag));

  const toggle = (tag: string) => {
    setSelectedTags((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) {
        next.delete(tag);
      } else {
        next.add(tag);
      }
      return next;
    });
  };

  const simulate = async () => {
    if (selectedTags.size === 0) return;
    setSubmitting(true);
    try {
      await client.post(`/devtools/documents/lead/${leadId}/documents`, {
        tags: Array.from(selectedTags),
      });
      toast({
        title: `${selectedTags.size} document(s) created`,
        status: 'success',
        duration: 3000,
      });
      setSelectedTags(new Set());
      onDone();
    } catch (err: any) {
      toast({
        title: 'Failed to create documents',
        description: err?.response?.data?.message || 'Unknown error',
        status: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const filteredTags = tags.filter(
    (tag) =>
      !filter ||
      tag.label.toLowerCase().includes(filter.toLowerCase()) ||
      tag.value.toLowerCase().includes(filter.toLowerCase()),
  );

  return (
    <Box p={3} borderWidth={1} borderRadius="md" borderColor="gray.200">
      <HStack mb={2} justify="space-between">
        <Text fontWeight="semibold" fontSize="sm">
          Add Documents by Tag
        </Text>
        {selectedTags.size > 0 && (
          <HStack spacing={2}>
            <Badge colorScheme="purple">{selectedTags.size} selected</Badge>
            <Button
              size="xs"
              variant="ghost"
              onClick={() => setSelectedTags(new Set())}
            >
              Clear
            </Button>
          </HStack>
        )}
      </HStack>

      <HStack mb={2} spacing={2} flexWrap="wrap">
        <Button
          size="xs"
          variant="outline"
          colorScheme="blue"
          onClick={() => setSelectedTags(new Set(requiredForSubmission))}
        >
          Select Required for Submission
        </Button>
        <Button
          size="xs"
          variant="outline"
          colorScheme="teal"
          onClick={() => {
            setSelectedTags(new Set(['csa', 'exhibit_a', 'bank_authorization', 'id_voided_check', 'rts_3']));
          }}
        >
          Select Enrollment Docs
        </Button>
      </HStack>

      <Input
        size="sm"
        placeholder="Filter tags..."
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        mb={2}
      />

      <Box maxH="200px" overflowY="auto" mb={2}>
        <SimpleGrid columns={2} spacing={1}>
          {filteredTags.map((tag) => {
            const exists = existingTags.has(tag.value);
            return (
              <Checkbox
                key={tag.value}
                size="sm"
                isChecked={selectedTags.has(tag.value)}
                onChange={() => toggle(tag.value)}
              >
                <Text
                  fontSize="xs"
                  color={exists ? 'green.600' : undefined}
                  fontWeight={exists ? 'semibold' : undefined}
                >
                  {tag.label}
                  {exists ? ' ✓' : ''}
                </Text>
              </Checkbox>
            );
          })}
        </SimpleGrid>
      </Box>

      <Button
        size="sm"
        colorScheme="purple"
        onClick={simulate}
        isLoading={submitting}
        isDisabled={selectedTags.size === 0}
      >
        Add {selectedTags.size || ''} Document{selectedTags.size !== 1 ? 's' : ''}
      </Button>
    </Box>
  );
}
